import React, { createContext, useState, useContext, useEffect } from "react";
import axios from "axios";
import { CircularProgress, Box } from "@mui/material";

const SettingsContext = createContext();
const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5001/api/v1";

// Cache settings in sessionStorage to reduce API calls
const SETTINGS_CACHE_KEY = "bgdatalinks_settings_cache";
const SETTINGS_CACHE_EXPIRY_KEY = "bgdatalinks_settings_cache_expiry";
const CACHE_TTL = 30 * 60 * 1000; // 30 minutes in milliseconds

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fetchAttempted, setFetchAttempted] = useState(false); // Track fetch attempts
  const [lastFetchTime, setLastFetchTime] = useState(0); // Throttle API calls

  const fetchSettings = async (force = false) => {
    // Prevent multiple fetch attempts on errors
    if (fetchAttempted && !force) return;

    // Check if we should use cached settings
    if (!force) {
      try {
        const cachedSettings = sessionStorage.getItem(SETTINGS_CACHE_KEY);
        const cacheExpiry = sessionStorage.getItem(SETTINGS_CACHE_EXPIRY_KEY);

        if (
          cachedSettings &&
          cacheExpiry &&
          Date.now() < parseInt(cacheExpiry)
        ) {
          setSettings(JSON.parse(cachedSettings));
          setLoading(false);
          return;
        }
      } catch (err) {
        console.log("Error retrieving from cache:", err);
        // Continue with API call if cache retrieval fails
      }
    }

    // Implement throttling - don't call API more than once per 10 seconds
    const now = Date.now();
    if (!force && now - lastFetchTime < 10000) {
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/settings/public`);
      setLastFetchTime(now);

      if (response.data?.status === "success") {
        const settingsData = response.data.data;
        setSettings(settingsData);

        // Cache the settings
        try {
          sessionStorage.setItem(
            SETTINGS_CACHE_KEY,
            JSON.stringify(settingsData)
          );
          sessionStorage.setItem(
            SETTINGS_CACHE_EXPIRY_KEY,
            (Date.now() + CACHE_TTL).toString()
          );
        } catch (err) {
          console.log("Error saving to cache:", err);
          // Non-critical error, continue without caching
        }
      }
      setFetchAttempted(true);
    } catch (err) {
      console.error("Settings fetch error:", err);
      setError(err.message);

      // Set default settings in case of error to prevent app failure
      setSettings({
        siteName: "Bgdatalinks",
        siteDescription: "Your trusted partner for digital services",
        contactEmail: "support@bgdatasub.com",
        supportPhone: "+234",
        address: "Kano, Nigeria",
        maintenanceMode: false,
        allowRegistration: true,
      });

      setFetchAttempted(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  // Show loading spinner instead of text
  if (loading && !settings) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={100}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <SettingsContext.Provider
      value={{
        settings,
        loading,
        error,
        refreshSettings: () => {
          setFetchAttempted(false); // Reset fetch attempt tracker before refreshing
          fetchSettings(true); // Force refresh
        },
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error("useSettings must be used within a SettingsProvider");
  }
  return context;
}
